import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { useAuth, formatApiErrorDetail, defaultConsolePath } from "@/context/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) navigate(from || defaultConsolePath(user.role), { replace: true });
  }, [user, navigate, from]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const session = await login(email.trim(), password);
      navigate(from || defaultConsolePath(session?.role), { replace: true });
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      if (err.response?.status === 403 || /not verified|not confirmed/i.test(detail)) {
        navigate(`/confirm?email=${encodeURIComponent(email.trim())}`, { replace: true });
        return;
      }
      setError(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell>
      <motion.form
        onSubmit={onSubmit}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <span className="chip-brand" data-testid="login-chip">
          Sign in
        </span>

        <h1 className="mt-6 font-display text-4xl sm:text-5xl font-bold text-white leading-tight tracking-tight">
          Welcome back
        </h1>
        <p className="mt-4 text-mymech-grey text-sm leading-relaxed">
          Access your garage, marketplace, and AI service guidance.
        </p>

        <div className="mt-10 space-y-5">
          <Field
            id="email"
            testId="login-email"
            label="Email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={setEmail}
          />
          <Field
            id="password"
            testId="login-password"
            label="Password"
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={setPassword}
          />
        </div>

        {error && (
          <div
            data-testid="login-error"
            className="mt-5 border border-mclaren/40 bg-mclaren/5 px-4 py-3 font-mono-tech text-[11px] uppercase tracking-widest text-mclaren"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          data-testid="login-submit"
          className="mt-8 w-full group inline-flex items-center justify-center gap-3 h-12 px-6 bg-mclaren text-void font-mono-tech text-[12px] uppercase tracking-[0.2em] hover:bg-sunset transition-all disabled:opacity-60"
        >
          {busy ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Authenticating
            </>
          ) : (
            <>
              Enter console <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </>
          )}
        </button>

        <p className="mt-6 text-white/50 text-sm">
          No account yet?{" "}
          <Link
            to="/signup"
            data-testid="login-signup-link"
            className="text-cyan-electric hover:text-white underline underline-offset-4"
          >
            Create one
          </Link>
        </p>
      </motion.form>
    </AuthShell>
  );
}

/** Shared auth-page layout: cinematic split with left brand column, right form. */
export function AuthShell({ children }) {
  return (
    <div className="min-h-screen bg-void text-white flex" style={{ overflowX: "clip" }}>
      {/* Left: brand column with ambient art */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden border-r border-white/5">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_60%,rgba(47,168,215,0.18),transparent_55%),radial-gradient(ellipse_at_80%_30%,rgba(238,242,245,0.06),transparent_50%)]" />
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
        />
        <div className="relative z-10 p-14 xl:p-20 flex flex-col justify-between w-full">
          <Link
            to="/"
            data-testid="auth-back-home"
            className="inline-flex items-center gap-2 text-sm text-mymech-grey hover:text-mymech-blue transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to home
          </Link>

          <div>
            <div className="mb-8">
              <BrandLogo tagline className="h-16 max-w-[280px]" />
            </div>
            <h2 className="font-display text-5xl xl:text-6xl font-bold text-white leading-tight tracking-tight">
              The mechanic<br />
              <span className="text-mymech-blue">you can trust.</span>
            </h2>
            <p className="mt-6 text-mymech-grey text-base max-w-md leading-relaxed">
              Choose a service, compare options, and book with clearer information and support throughout your journey.
            </p>
          </div>

          <p className="text-sm text-mymech-grey">
            Reliable auto care, easier to find and book.
          </p>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-16 relative">
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
        />
        {children}
      </div>
    </div>
  );
}

export function Field({ id, testId, label, value, onChange, type = "text", required, autoComplete }) {
  return (
    <label htmlFor={id} className="block">
      <span className="block font-mono-tech text-[10px] uppercase tracking-widest text-white/50 mb-2">
        {label}
      </span>
      <input
        id={id}
        data-testid={testId}
        type={type}
        required={required}
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-mymech-charcoal/80 border border-white/15 rounded-brand focus:border-mymech-blue focus:ring-0 focus:outline-none text-white placeholder-mymech-grey px-4 h-12 text-sm transition-colors"
      />
    </label>
  );
}
